import { Immutable } from "immer";
import React from "react";
import { PDPChangeRootProductBlock } from "tsi-common-react/src/apps/configurator/PDPChangeRootProductBlock/index";
import { PDPChangeVariantOptionBlock } from "tsi-common-react/src/apps/configurator/PDPChangeVariantOptionBlock/index";
import { PDPSelectedRootProductConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedRootProductConditionalBlock/index";
import { PDPSelectedVariantConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedVariantConditionalBlock/index";
import { ProductReviewsSSR } from "tsi-common-react/src/apps/reviews/ProductReviewsSSR/index";
import { CompatibilityAccordion } from "tsi-common-react/src/components/CompatibilityAccordion/index";
import { FeaturesBlock } from "tsi-common-react/src/components/FeaturesBlock/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { IOptionCode } from "tsi-common-react/src/models/catalogue";
import { isoProductID } from "tsi-common-react/src/models/nominals";

import { templates } from "@reactivated";
import type {
    CarouselModule as CarouselModuleValue,
    ChangeVariantOptionBlock as ChangeVariantOptionBlockValue,
    CompatibilityAccordionModule as CompatibilityAccordionValue,
    DetailsModule as DetailsModuleValue,
    FullReviewsModule as FullReviewsModuleValue,
    RawHTMLBlock as RawHTMLBlockValue,
    SelectedRootProductConditionalBlock as RootConditionalBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
    SelectedVariantConditionalBlock as VariantConditionalBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CarouselModule } from "../blocks/CarouselModule";
import { DetailsModule } from "../blocks/DetailsModule";
import { toolboxComponentsMap } from "../blocks/toolboxComponentsMap";

/**
 * Shared assembly for the consolidated toppers ProductLinePage body
 * (``PDPModuleAssembly``). This dispatches the page ``modules``
 * StreamField -- the hero chrome above the body
 * (``ProductHeroAssemblyTemplate`` + ``{% modular_configurator %}``) is rendered
 * separately and is NOT part of this assembly.
 *
 * The shared ``pdp-change-variant-option-block.html`` renders BOTH the
 * change-ROOT selector (the tab CONTROLS that switch the
 * ``root_product_conditional_block`` tabpanels) AND the change-VARIANT selector
 * inside its ``#pdp-change-variant-block`` wrapper, so a single
 * ``change_variant_option_block`` carries TWO id-keyed side-channels
 * (``changeRootSSR`` + ``changeVariantSSR``) keyed by the same ``block.id``.
 *
 * The base block set comes from ``toolboxComponentsMap`` (the 11 Toolbox keys,
 * including the recursive ``promo_conditional_block`` and the SSR-envelope
 * grids). Layered on top are the PLP module keys
 * (``carousel`` / ``compatibility`` / ``details`` / ``HTML`` / ``reviews_full`` /
 * ``value_prop_bar``) and the three configurator-driven keys
 * (``change_variant_option_block`` / ``root_product_conditional_block`` /
 * ``variant_conditional_block``).
 *
 * TOPPERS DELTAS vs the toolbox spread (verified against the generated
 * ``PDPModuleAssembly`` union in ``@reactivated``):
 *   - ``features`` is a ``FeaturesBlock`` here (BasicPDPModuleAssembly override),
 *     NOT toppers' ``FeaturesModule``. The toolbox map maps ``features`` ->
 *     ``FeaturesModule``, so we MUST override it back to the shared
 *     ``FeaturesBlock``.
 *
 * Three blocks are page-dependent: the reactivated body-serialization path
 * threads ``request`` but not ``page``, so their server-computed SSR props are
 * threaded via id-keyed side-channels (``reviews`` / ``changeVariantSSR`` /
 * ``rootConditionalSSR`` / ``variantConditionalSSR``) and looked up here by
 * ``block.id``. (The ``reviews`` lookup additionally carries
 * ``page.num_reviews`` as ``pageNumReviews`` for the CTA gate.)
 */
type IProps = Immutable<{
    value: NonNullable<
        templates.ProductLinePageTemplate["page"]["modules"]
    >["value"];
    reviews: {
        [k: string]: templates.ProductReviewsTemplate;
    };
    pageNumReviews: number;
    changeVariantSSR: {
        [k: string]: templates.ChangeVariantOptionBlockTemplate;
    };
    changeRootSSR: {
        [k: string]: templates.ChangeRootProductBlockTemplate;
    };
    rootConditionalSSR: {
        [k: string]: templates.SelectedRootProductConditionalBlockTemplate;
    };
    variantConditionalSSR: {
        [k: string]: templates.SelectedVariantConditionalBlockTemplate;
    };
}>;

// PDP star-reveal scroll effect: a ScrollMagic scene keyed to the `#reviews`
// trigger that toggles `.star-rating__star--active` on every rating star once
// `#reviews` reaches the viewport's vertical midpoint. The scene's `duration: 0`
// makes this an instant class flip at that scroll point (not a tween), and
// `triggerHook: 0.5` is the midpoint offset -- both preserved from the legacy
// setup. ScrollMagic is dynamically imported INSIDE the effect so it never loads
// under SSR, and the controller (and its scene) are destroyed on unmount.
const PdpReviewsScrollSection = (props: {
    children: React.ReactNode;
}): React.JSX.Element => {
    const triggerRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!triggerRef.current) {
            return;
        }
        let cancelled = false;
        let controller: { destroy: (resetScenes: boolean) => void } | null =
            null;
        void import("scrollmagic").then((mod) => {
            const ScrollMagic = mod.default;
            const trigger = triggerRef.current;
            if (cancelled || !trigger) {
                return;
            }
            const ctrl = new ScrollMagic.Controller({
                globalSceneOptions: {
                    duration: 0,
                },
            });
            controller = ctrl;
            new ScrollMagic.Scene({
                triggerElement: trigger,
                triggerHook: 0.5,
            })
                .setClassToggle(
                    ".star-rating__star",
                    "star-rating__star--active",
                )
                .addTo(ctrl);
        });
        return () => {
            cancelled = true;
            controller?.destroy(true);
        };
    }, []);
    return (
        <div id="reviews" ref={triggerRef}>
            {props.children}
        </div>
    );
};

export const ProductModulesBody = (props: IProps): React.JSX.Element => {
    const components = {
        ...toolboxComponentsMap,

        // `carousel` -> toppers CarouselModule.
        carousel: (block: { value: Immutable<CarouselModuleValue> }) => (
            <CarouselModule value={block.value} />
        ),

        // `details` -> toppers DetailsModule.
        details: (block: { value: Immutable<DetailsModuleValue> }) => (
            <DetailsModule value={block.value} />
        ),

        // `features` -> shared FeaturesBlock (self-contained `value` dispatch).
        // CRITICAL: this OVERRIDES the toolbox spread's `features` ->
        // FeaturesModule. In `PDPModuleAssembly` the `features` value is a
        // `FeaturesBlock` (BasicPDPModuleAssembly redeclares it), so the toppers
        // FeaturesModule renderer would be the wrong component here.
        features: FeaturesBlock,

        // `compatibility` -> CompatibilityAccordion wrapped in the themed
        // `<section>` that the standalone `compatibility-accordion-module.html`
        // emits (`<section class="{{ value.background_style.style }}">`). The
        // wrapper MUST live here, NOT inside the component / template re-export,
        // because the per-block `{% include_block %}` path already wraps
        // it -- wrapping inside the component would double-wrap. The
        // consolidated dispatcher reaches the component raw (bypassing the
        // `.html`), so it owns the wrapper.
        compatibility: (block: {
            value: Immutable<CompatibilityAccordionValue>;
        }) => (
            <section className={block.value.background_style.style}>
                <CompatibilityAccordion value={block.value} />
            </section>
        ),

        // `HTML` (uppercase key) -> shared RawHTMLBlock (raw HTML string value).
        // The toppers StreamField names the block `HTML`.
        HTML: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),

        // `value_prop_bar` -> ValuePropsBarBlock (prop is `valuePropsData`).
        // Same prop-rename wrapper as the toolbox `value_prop_bar_block` entry;
        // both keys exist in `PDPModuleAssembly` and both render ValuePropsBar.
        value_prop_bar: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,

        // `reviews_full` -> reproduces
        // `cms/components/pdp-reviews-full.html`: a `.pdp-section` wrapper, the
        // `#reviews` SSR reviews (gated on `reviews_brand_id`, matching
        // `{% if page.reviews_brand_id %}`), and the optional CTA. The SSR
        // props are looked up per block by `block.id` from the id-keyed
        // `props.reviews` side-channel (the StreamBlock has no `max_num` cap, so
        // each reviews block renders with its own products / title / filter
        // data). The CTA is gated on `value.show_cta && page.num_reviews > 0`
        // (uses `page.num_reviews`, threaded here as
        // `props.pageNumReviews`, NOT the per-block `reviews[id].num_reviews`).
        // No `brand` override is passed: toppers renders reviews
        // with the shared default brand label (the toppers
        // ProductReviewsTemplate re-export passes no `brand`).
        reviews_full: (block: {
            id?: string;
            value: Immutable<FullReviewsModuleValue>;
        }) => {
            const reviews = block.id ? props.reviews[block.id] : undefined;
            return (
                <div className="pdp-section">
                    {reviews && reviews.reviews_brand_id && (
                        <>
                            <PdpReviewsScrollSection>
                                <ProductReviewsSSR
                                    {...reviews}
                                    isCollapsedView={true}
                                />
                            </PdpReviewsScrollSection>
                            {block.value.show_cta &&
                                props.pageNumReviews > 0 && (
                                    <div className="pdp-reviews-full__cta">
                                        <a
                                            className="button button--ghost pdp-reviews-full__cta-link"
                                            href={block.value.cta?.url ?? ""}
                                        >
                                            {block.value.cta_text}
                                        </a>
                                    </div>
                                )}
                        </>
                    )}
                </div>
            );
        },

        // `change_variant_option_block` -> reproduces the
        // `pdp-change-variant-option-block.html` wrapper (`#pdp-change-variant-
        // block` + optional `|safe` title) around BOTH the change-ROOT and the
        // change-VARIANT selectors. The template `include_react`s the
        // change-ROOT component (the tab CONTROLS that switch the
        // `root_product_conditional_block` tabpanels) BEFORE the change-VARIANT
        // component, so we render them in the same order (title -> root ->
        // variant), both inside this wrapper. Both selectors' SSR props are keyed
        // by the SAME `block.id`: the root from `changeRootSSR`, the variant from
        // `changeVariantSSR`. PDPChangeRootProductBlock self-guards (renders null
        // when < 2 root products), so it's a no-op on single-root PLPs.
        // `optionInputType` / `title` come from the block value.
        change_variant_option_block: (block: {
            id?: string;
            value: Immutable<ChangeVariantOptionBlockValue>;
        }) => {
            const ssr = block.id ? props.changeVariantSSR[block.id] : undefined;
            const rootSSR = block.id
                ? props.changeRootSSR[block.id]
                : undefined;
            const optionInputType = block.value.optionInputType ?? "";
            return (
                <div
                    tabIndex={0}
                    id="pdp-change-variant-block"
                    className={`pdp-change-variant-option-block l-capped-width ${optionInputType}`}
                >
                    {block.value.title && (
                        <h2
                            className="pdp-change-variant-option-block__title"
                            dangerouslySetInnerHTML={{
                                __html: block.value.title,
                            }}
                        />
                    )}
                    {rootSSR && (
                        <PDPChangeRootProductBlock
                            inputType={rootSSR.input_type}
                            initialRootProductsData={
                                rootSSR.initial_root_products_data
                            }
                            initialSelectedRootProductId={
                                rootSSR.initial_selected_root_product_id
                            }
                        />
                    )}
                    {ssr && (
                        <PDPChangeVariantOptionBlock
                            attributeCode={ssr.attribute_code as IOptionCode}
                            inputType={ssr.input_type}
                            initialOptionValuesData={
                                ssr.initial_option_values_data
                            }
                            initialSelectedValue={ssr.initial_selected_value}
                        />
                    )}
                </div>
            );
        },

        // `root_product_conditional_block` -> PDPSelectedRootProductConditional
        // Block. The `pdp-selected-root-product-conditional-block.html`
        // adds no wrapper (the container emits its own gated tabpanel div), so
        // dispatch directly. The gating props (block product id + initial
        // visibility) come from the id-keyed `rootConditionalSSR` side-channel;
        // the block's `content` (a `RootProductConditionalBlockContent`
        // StreamBlock) is rendered as real React children through this same
        // `components` map -- replacing the JSON
        // `content_segments` / `dangerouslySetInnerHTML` path so every nested
        // block hydrates. `components` is captured by reference below
        // (define-then-close-over), exactly like the recursive
        // `promo_conditional_block`. `RootProductConditionalBlockContent` is the
        // basic set PLUS `variant_conditional_block`, both of which resolve in
        // this map (the variant entry is defined below).
        root_product_conditional_block: (block: {
            id?: string;
            value: Immutable<RootConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.rootConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedRootProductConditionalBlock
                    blockProductID={isoProductID.wrap(ssr.block_product_id)}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedRootProductConditionalBlock>
            );
        },

        // `variant_conditional_block` -> PDPSelectedVariantConditionalBlock. The
        // `pdp-selected-variant-conditional-block.html` adds no wrapper
        // (the container emits its own gated tabpanel div). Gating
        // (attribute_code + attribute_value + initial visibility) comes from the
        // id-keyed `variantConditionalSSR` side-channel; the block's `content`
        // (a `BasicPDPModuleAssembly` -- no further conditionals) is rendered as
        // real React children through this same `components` map. The
        // shared container was extended to accept `children` (backward-compatibly
        // -- the `contentSegments` path still works when children are
        // absent).
        variant_conditional_block: (block: {
            id?: string;
            value: Immutable<VariantConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.variantConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedVariantConditionalBlock
                    attributeCode={ssr.attribute_code as IOptionCode}
                    attributeValue={ssr.attribute_value}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedVariantConditionalBlock>
            );
        },
    };
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.value}
            components={components}
        />
    );
};
